/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Popover,
  Button,
  Avatar,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import useCollectorUsers from '../../../../../hooks/useCollectorUsers';
import { getUserAvatar } from '../../../../../services/identity/getUserAvatar';
import { ClientTag, UserProfile, UserRoleName } from '../../../../../services/identity/types/UserProfile';
import { userStatusOptions, patientTypeOptions } from '@brands/Utils/selectOptions';
import { Loading } from '../../../../../Components/LoadingSpinner/Loading';
import styles from '../../styles.module.scss';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import {
  ColumnVisibilityKey,
  setColumnVisibility as setPersistedColumnVisibility,
} from '@brands/store/slices/columnVisibilitySlice';
import {
  TablePaginationKey,
  setRowsPerPage as setPersistedRowsPerPage,
} from '@brands/store/slices/tablePaginationSlice';
import useDebounce from '@brands/hooks/useDebounce';
import {
  getSessionArray,
  getSessionNumber,
  getSessionString,
  readSessionTableState,
  writeSessionTableState,
} from '@brands/Utils/sessionTableState';
import { listAllClientTags } from '../../../../../services/identity/listAllClientTags';
import { getClientDivisionOptions } from '../../../../../services/identity/clientDivisionOptions';
import useWindowSize from '../../../../../hooks/useWindowSize';

const COLUMNS = [
  { key: 'full_name', label: 'Name' },
  { key: 'external_id', label: 'External ID' },
  { key: 'id', label: 'Internal ID' },
  { key: 'email', label: 'Email address' },
  { key: 'client_tags', label: 'Client Tag(s)' },
  { key: 'client_divisions', label: 'Division(s)' },
  { key: 'status', label: 'Status' },
  { key: 'type', label: 'Type' },
] as const;
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = 25;

type ColumnKey = (typeof COLUMNS)[number]['key'];

const headerCellSx = {
  backgroundColor: '#eeeeee',
  fontSize: 12,
  fontWeight: 600,
  padding: '8px 12px',
};

const bodyCellSx = {
  fontSize: 13,
  padding: '10px 12px',
};

const filterLabelSx = {
  fontSize: 12,
  fontWeight: 400,
};

const SORT_BY_MAP: Record<ColumnKey, string> = {
  full_name: 'full_name',
  external_id: 'external_id',
  id: 'id',
  email: 'email',
  client_tags: 'client_tags',
  client_divisions: 'client_divisions',
  status: 'status',
  type: 'type',
};

const CHAR_PX = 7.6;
// Reserve room for sort arrow in header.
const COL_PAD_PX = 48;
const COL_MIN_PX = 120;
const COL_MAX_PX = 400;

function columnWidthPx(label: string, cellStrings: string[]): number {
  const longest = [label, ...cellStrings].reduce((m, s) => Math.max(m, (s ?? '').length), 0);
  return Math.min(COL_MAX_PX, Math.max(COL_MIN_PX, Math.ceil(longest * CHAR_PX + COL_PAD_PX)));
}

function cellStringForUserColumn(key: ColumnKey, row: UserProfile): string {
  switch (key) {
    case 'full_name':
      return row.full_name ?? '-';
    case 'external_id':
      return row.external_id ?? '-';
    case 'id':
      return String(row.id);
    case 'email':
      return row.email ?? '-';
    case 'client_tags':
      if (row.clients?.length) return row.clients.map((c) => c.name).join(', ');
      if (row.markers?.length) {
        return row.markers
          .map((m) => (typeof m === 'object' && m !== null && 'value' in m ? (m as { value: string }).value : m))
          .join(', ');
      }
      return '-';
    case 'client_divisions':
      return row.divisions?.length ? row.divisions.map((d) => d.name).join(', ') : '-';
    case 'status':
      return row.status ?? '-';
    case 'type': {
      const name = row.role?.name ?? row.type?.name ?? '';
      return name === UserRoleName.Dependent ?
          'Dependent'
        : name === UserRoleName.Patient ?
          'Patient'
        : name || '-';
    }
    default:
      return '-';
  }
}

function buildUserColumnWidths(rows: UserProfile[]): Record<ColumnKey, number> {
  const out = {} as Record<ColumnKey, number>;
  COLUMNS.forEach((c) => {
    const values = rows.map((row) => cellStringForUserColumn(c.key, row));
    out[c.key] = columnWidthPx(c.label, values.length ? values : ['—']);
  });
  return out;
}

const EllipsisCell = ({ text }: { text: string }) => (
  <Tooltip
    title={text}
    placement="top-start"
    enterDelay={400}
    disableHoverListener={!text || text === '-'}
  >
    <Box
      component="span"
      sx={{
        display: 'block',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </Box>
  </Tooltip>
);

const STATUS_PILL_STYLES: Record<string, { color: string; backgroundColor: string }> = {
  Active: { color: '#166534', backgroundColor: '#DCFCE7' },
  Invited: { color: '#C2410C', backgroundColor: '#FFEDD5' },
  'Not Invited': { color: '#B91C1C', backgroundColor: '#FEE2E2' },
  Pending: { color: '#92400E', backgroundColor: '#FEF3C7' },
  Deactivated: { color: '#6B7280', backgroundColor: '#F3F4F6' },
};

const getStatusStyle = (status: string) =>
  STATUS_PILL_STYLES[status] ?? { color: '#374151', backgroundColor: '#F3F4F6' };

const toBackendStatus = (s: string): string => s.toLowerCase().replace(/\s+/g, '_');

const SESSION_STATE_KEY = 'dentistry-one:collector:users';

const FilterCell = ({ children, widthPx }: { children: React.ReactNode; widthPx: number }) => (
  <TableCell
    sx={{
      background: '#FFF',
      padding: '4px 8px',
      borderRight: '1px solid #E5E7EB',
      width: widthPx,
      minWidth: widthPx,
      maxWidth: widthPx,
      verticalAlign: 'bottom',
      boxSizing: 'border-box',
    }}
  >
    {children}
  </TableCell>
);

const SortableHeader = ({
  label,
  column,
  sortBy,
  sortDirection,
  onSort,
  isLast,
  widthPx,
  isMobile,
}: {
  label: string;
  column: ColumnKey;
  sortBy: ColumnKey | null;
  sortDirection: 'asc' | 'desc' | null;
  onSort: (c: ColumnKey) => void;
  isLast: boolean;
  widthPx: number;
  isMobile: boolean;
}) => (
  <TableCell
    sx={{
      ...headerCellSx,
      cursor: 'pointer',
      borderRight: isLast ? 'none' : '1px solid #E5E7EB',
      width: widthPx,
      minWidth: widthPx,
      maxWidth: widthPx,
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}
    onClick={() => onSort(column)}
  >
    <Tooltip title={label} placement="top-start" enterDelay={500}>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        width="100%"
        sx={{
          overflow: 'hidden',
          minWidth: 0,
          '&:hover .collector-users-sort-arrow': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {label}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto',
          }}
        >
          {sortBy === column && sortDirection === 'desc' ? (
            <ArrowDownwardIcon
              fontSize="small"
              className="collector-users-sort-arrow"
              sx={{
                opacity: 1,
                pointerEvents: 'auto',
                transition: 'opacity 0.2s ease',
              }}
            />
          ) : (
            <ArrowUpwardIcon
              fontSize="small"
              className="collector-users-sort-arrow"
              sx={{
                opacity: isMobile || sortBy === column ? 1 : 0,
                pointerEvents: isMobile || sortBy === column ? 'auto' : 'none',
                transition: 'opacity 0.2s ease',
              }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  </TableCell>
);

const getInitials = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
  }
  return fullName.slice(0, 2).toUpperCase();
};

const Users = (): JSX.Element => {
  const navigate = useNavigate();
  const savedState = useMemo(() => readSessionTableState(SESSION_STATE_KEY), []);
  const rowsPerPageHydratedRef = useRef(false);
  const { userRole } = useParams();
  const { userInfo } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const initialSortBy = getSessionString(savedState, 'sort_by', 'full_name') as ColumnKey;
  const [sortBy, setSortBy] = useState<ColumnKey | null>(
    COLUMNS.some((column) => column.key === initialSortBy) ? initialSortBy : 'full_name',
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    getSessionString(savedState, 'sort_direction') === 'desc' ? 'desc' : 'asc',
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const rowsPerPageOverride = useAppSelector(
    (s) => s.tablePagination[TablePaginationKey.CollectorUsers] ?? null,
  );
  const rowsPerPage = ROWS_PER_PAGE_OPTIONS.includes(rowsPerPageOverride ?? -1)
    ? (rowsPerPageOverride as number)
    : DEFAULT_ROWS_PER_PAGE;

  useEffect(() => {
    if (rowsPerPageOverride === null || ROWS_PER_PAGE_OPTIONS.includes(rowsPerPageOverride)) return;
    dispatch(
      setPersistedRowsPerPage({
        tableKey: TablePaginationKey.CollectorUsers,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
      }),
    );
  }, [dispatch, rowsPerPageOverride]);

  const persistedColumnVisibility = useAppSelector(
    (s) => s.columnVisibility[ColumnVisibilityKey.CollectorUsers],
  );
  const defaultColumnVisibility = useMemo(
    () => COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {} as Record<ColumnKey, boolean>),
    [],
  );
  const columnVisibility = useMemo(
    () =>
      ({ ...defaultColumnVisibility, ...(persistedColumnVisibility ?? {}) }) as Record<
        ColumnKey,
        boolean
      >,
    [defaultColumnVisibility, persistedColumnVisibility],
  );
  const setColumnVisibility = useCallback(
    (model: Record<ColumnKey, boolean>) => {
      dispatch(setPersistedColumnVisibility({ tableKey: ColumnVisibilityKey.CollectorUsers, model }));
    },
    [dispatch],
  );
  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    full_name: getSessionString(savedState, 'full_name'),
    external_id: getSessionString(savedState, 'external_id'),
    id: getSessionString(savedState, 'id'),
    email: getSessionString(savedState, 'email'),
    status: getSessionArray(savedState, 'status'),
    type: getSessionArray(savedState, 'type'),
    client_tags: getSessionArray(savedState, 'client_tags'),
  });
  const [clientTagOptions, setClientTagOptions] = useState<{ value: string; label: string }[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);

  useEffect(() => {
    listAllClientTags(UserRoleName.Collector).then(({ nodes }) => {
      const withCode = nodes.filter((t) => Boolean(t.code));
      setClientTagOptions(withCode.map((t) => ({ value: t.code as string, label: t.name })));
      setAllClientTags(withCode);
    });
  }, []);

  const selectedClientTagIdsForDivision = useMemo(
    () => allClientTags.filter((t) => (filterValues.client_tags || []).includes(t.code || '')).map((t) => t.id),
    [allClientTags, filterValues.client_tags],
  );

  const divisionFilterOptions = useMemo(
    () => getClientDivisionOptions(allClientTags, selectedClientTagIdsForDivision),
    [allClientTags, selectedClientTagIdsForDivision],
  );

  const [page, setPage] = useState(() => Math.max(1, getSessionNumber(savedState, 'page', 1)));

  const updateListState = useCallback(
    (
      overrides: Partial<{
        page: number;
        rowsPerPage: number;
        filters: Record<string, any>;
        sortBy: ColumnKey | null;
        sortDirection: 'asc' | 'desc';
      }> = {},
    ) => {
      const nextFilters = overrides.filters ?? filterValues;
      writeSessionTableState(SESSION_STATE_KEY, {
        ...nextFilters,
        page: overrides.page ?? page,
        rowsPerPage: overrides.rowsPerPage ?? rowsPerPageOverride ?? undefined,
        sort_by: overrides.sortBy ?? sortBy ?? undefined,
        sort_direction: overrides.sortDirection ?? sortDirection,
      });
    },
    [filterValues, page, rowsPerPageOverride, sortBy, sortDirection],
  );

  useEffect(() => {
    if (rowsPerPageHydratedRef.current) return;
    rowsPerPageHydratedRef.current = true;
    const nextRowsPerPage = getSessionNumber(savedState, 'rowsPerPage', rowsPerPage);
    if (nextRowsPerPage !== rowsPerPage && ROWS_PER_PAGE_OPTIONS.includes(nextRowsPerPage)) {
      dispatch(
        setPersistedRowsPerPage({
          tableKey: TablePaginationKey.CollectorUsers,
          rowsPerPage: nextRowsPerPage,
        }),
      );
    }
  }, [dispatch, rowsPerPage, savedState]);

  const baseParams = useMemo(
    () => ({
      limit: rowsPerPage,
      full_name: filterValues.full_name || undefined,
      external_id: filterValues.external_id || undefined,
      id: filterValues.id || undefined,
      email: filterValues.email || undefined,
      status:
        filterValues.status?.length ?
          filterValues.status.map((s: string) => toBackendStatus(s))
        : undefined,
      type: filterValues.type?.length ? filterValues.type : undefined,
      client_tags: filterValues.client_tags?.length ? filterValues.client_tags : undefined,
      client_division_ids: filterValues.client_division_ids?.length
        ? filterValues.client_division_ids.map(Number)
        : undefined,
      sort_by:
        sortBy ?
          // client_divisions has no server-side sort support yet; the cast below intentionally
          // excludes it from the allowed literals — clicking that header still no-ops rather than
          // sending an unsupported sort_by value.
          (SORT_BY_MAP[sortBy] as 'full_name' | 'email' | 'external_id' | 'id' | 'status' | 'type' | 'client_tags')
        : undefined,
      sort_direction: sortDirection,
    }),
    [rowsPerPage, filterValues, sortBy, sortDirection],
  );

  const debouncedParams = useDebounce(baseParams, 400);

  const collectorParams = useMemo(
    () => ({
      ...debouncedParams,
      skip: (page - 1) * rowsPerPage,
    }),
    [debouncedParams, page, rowsPerPage],
  );

  const { users, totalRecords, dataLoading } = useCollectorUsers(collectorParams);

  const columnWidths = useMemo(() => buildUserColumnWidths(users), [users]);

  const visibleColumns = useMemo(() => COLUMNS.filter((c) => columnVisibility[c.key]), [columnVisibility]);

  const handleSort = (column: ColumnKey) => {
    const nextSortDirection = sortBy === column && sortDirection === 'asc' ? 'desc' : 'asc';
    if (sortBy !== column) {
      setSortBy(column);
      setSortDirection('asc');
      updateListState({ sortBy: column, sortDirection: 'asc', page: 1 });
      setPage(1);
      return;
    }
    setSortDirection(nextSortDirection);
    setPage(1);
    updateListState({ sortBy: column, sortDirection: nextSortDirection, page: 1 });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => {
      const next = { ...prev, [key]: value };
      updateListState({ filters: next, page: 1 });
      return next;
    });
    setPage(1);
  };

  const displayClientTags = (user: UserProfile): string => {
    if (user.clients?.length) return user.clients.map((c) => c.name).join(', ');
    if (user.markers?.length) return user.markers.map((m) => (typeof m === 'object' && m !== null && 'value' in m ? (m as any).value : m)).join(', ');
    return '-';
  };

  const displayClientDivisions = (user: UserProfile): string =>
    user.divisions?.length ? user.divisions.map((d) => d.name).join(', ') : '-';

  const displayType = (user: UserProfile): string => {
    const name = user.role?.name ?? user.type?.name ?? '';
    return name === UserRoleName.Dependent ? 'Dependent' : name === UserRoleName.Patient ? 'Patient' : name || '-';
  };

  const basePath = userRole ? `/${userRole}` : '';

  if (dataLoading && users.length === 0) {
    return <Loading dotted />;
  }

  return (
    <div style={{ width: '100%', paddingBottom: '64px' }}>
      <div className={styles.providerDashboarTableHeader}>
        <div className={styles.providerDashboarTableHeaderTop}>
          {/* An h1 in the design; this was the third page heading marked up as a span. */}
          <h1 className={styles.providerDashboarTableHeaderText}>Users</h1>
          <Button
            variant="contained"
            size="medium"
            className={styles.collectorPrimaryPillButton}
            startIcon={<AddIcon />}
            onClick={() => navigate(`${basePath}/users/new`)}
          >
            Add new user
          </Button>
        </div>
        <div className={styles.providerDashboarManageColumnsRow}>
          <Button
            startIcon={<ViewColumnIcon />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size="small"
            className={styles.manageColumnsBtn}
          >
            Manage columns
          </Button>
        </div>
      </div>

      <Box sx={{ width: '100%' }}>
        <Paper className={styles.providerTablePaper} sx={{ border: '1px solid #E5E7EB' }}>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Box p={2}>
              {COLUMNS.map((c) => (
                <Box key={c.key} display="flex" alignItems="center">
                  <Checkbox
                    checked={columnVisibility[c.key]}
                    onChange={() =>
                      setColumnVisibility({
                        ...columnVisibility,
                        [c.key]: !columnVisibility[c.key],
                      })
                    }
                  />
                  {c.label}
                </Box>
              ))}
            </Box>
          </Popover>

          <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)', overflowX: 'auto' }}>
            <Table
              stickyHeader
              size="medium"
              sx={{ width: 'max-content', minWidth: '100%', tableLayout: 'fixed' }}
            >
              <TableHead>
                <TableRow className={styles.tableHeaderRow}>
                  {visibleColumns.map((c, index) => (
                    <SortableHeader
                      key={c.key}
                      label={c.label}
                      column={c.key}
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      isLast={index === visibleColumns.length - 1}
                      widthPx={columnWidths[c.key]}
                      isMobile={isMobile}
                    />
                  ))}
                </TableRow>
                <TableRow className={styles.tableFilterRow}>
                  {columnVisibility.full_name && (
                    <FilterCell widthPx={columnWidths.full_name}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label="Name"
                        value={filterValues.full_name || ''}
                        onChange={(e) => handleFilterChange('full_name', e.target.value)}
                        InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
                        InputLabelProps={{ shrink: Boolean(filterValues.full_name), sx: filterLabelSx }}
                      />
                    </FilterCell>
                  )}
                  {columnVisibility.external_id && (
                    <FilterCell widthPx={columnWidths.external_id}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label="External ID"
                        value={filterValues.external_id || ''}
                        onChange={(e) => handleFilterChange('external_id', e.target.value)}
                        InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
                        InputLabelProps={{ shrink: Boolean(filterValues.external_id), sx: filterLabelSx }}
                      />
                    </FilterCell>
                  )}
                  {columnVisibility.id && (
                    <FilterCell widthPx={columnWidths.id}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label="Internal ID"
                        value={filterValues.id || ''}
                        onChange={(e) => handleFilterChange('id', e.target.value)}
                        InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
                        InputLabelProps={{ shrink: Boolean(filterValues.id), sx: filterLabelSx }}
                      />
                    </FilterCell>
                  )}
                  {columnVisibility.email && (
                    <FilterCell widthPx={columnWidths.email}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label="Email address"
                        value={filterValues.email || ''}
                        onChange={(e) => handleFilterChange('email', e.target.value)}
                        InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
                        InputLabelProps={{ shrink: Boolean(filterValues.email), sx: filterLabelSx }}
                      />
                    </FilterCell>
                  )}
                  {columnVisibility.client_tags && (
                    <FilterCell widthPx={columnWidths.client_tags}>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel shrink={(filterValues.client_tags || []).length > 0} sx={filterLabelSx}>
                          Client tags
                        </InputLabel>
                        <Select
                          multiple
                          value={filterValues.client_tags || []}
                          onChange={(e) => {
                            setFilterValues((prev) => {
                              const next = { ...prev, client_tags: e.target.value, client_division_ids: undefined };
                              updateListState({ filters: next, page: 1 });
                              return next;
                            });
                            setPage(1);
                          }}
                          disableUnderline
                          sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((v) => clientTagOptions.find((o) => o.value === v)?.label)
                              .join(', ')
                          }
                        >
                          {clientTagOptions.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              <Checkbox size="small" checked={(filterValues.client_tags || []).includes(o.value)} />
                              <ListItemText primary={o.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterCell>
                  )}
                  {columnVisibility.client_divisions && (
                    <FilterCell widthPx={columnWidths.client_divisions}>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel
                          shrink={(filterValues.client_division_ids || []).length > 0}
                          sx={filterLabelSx}
                        >
                          {(filterValues.client_tags || []).length ? 'Division(s)' : 'Select a client tag first'}
                        </InputLabel>
                        <Select
                          multiple
                          disabled={!(filterValues.client_tags || []).length}
                          value={filterValues.client_division_ids || []}
                          onChange={(e) => handleFilterChange('client_division_ids', e.target.value)}
                          disableUnderline
                          sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((v) => divisionFilterOptions.find((o) => o.id === v)?.label)
                              .join(', ')
                          }
                        >
                          {divisionFilterOptions.map((o) => (
                            <MenuItem key={o.id} value={o.id}>
                              <Checkbox
                                size="small"
                                checked={(filterValues.client_division_ids || []).includes(o.id)}
                              />
                              <ListItemText primary={o.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterCell>
                  )}
                  {columnVisibility.status && (
                    <FilterCell widthPx={columnWidths.status}>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel shrink={(filterValues.status || []).length > 0} sx={filterLabelSx}>
                          Status
                        </InputLabel>
                        <Select
                          multiple
                          value={filterValues.status || []}
                          onChange={(e) => handleFilterChange('status', e.target.value)}
                          disableUnderline
                          sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((v) => userStatusOptions.find((o) => o.value === v)?.label)
                              .join(', ')
                          }
                        >
                          {userStatusOptions.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              <Checkbox size="small" checked={(filterValues.status || []).includes(o.value)} />
                              <ListItemText primary={o.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterCell>
                  )}
                  {columnVisibility.type && (
                    <FilterCell widthPx={columnWidths.type}>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel shrink={(filterValues.type || []).length > 0} sx={filterLabelSx}>
                          Patient, Dependent
                        </InputLabel>
                        <Select
                          multiple
                          value={filterValues.type || []}
                          onChange={(e) => handleFilterChange('type', e.target.value)}
                          disableUnderline
                          sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((v) => patientTypeOptions.find((o) => o.value === v)?.label)
                              .join(', ')
                          }
                        >
                          {patientTypeOptions.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              <Checkbox size="small" checked={(filterValues.type || []).includes(o.value)} />
                              <ListItemText primary={o.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((row) => {
                    const statusText = row.status || '-';
                    const goToUser = () => navigate(`${basePath}/users/${row.id}`);
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        tabIndex={0}
                        onClick={goToUser}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            goToUser();
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        {columnVisibility.full_name && (
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              width: columnWidths.full_name,
                              minWidth: columnWidths.full_name,
                              maxWidth: columnWidths.full_name,
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, width: '100%' }}>
                              <Avatar
                                src={getUserAvatar(row.id)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  fontSize: 12,
                                  bgcolor: '#E5E7EB',
                                  color: '#6B7280',
                                  flexShrink: 0,
                                }}
                              >
                                {getInitials(row.full_name || '')}
                              </Avatar>
                              <EllipsisCell text={row.full_name || '-'} />
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.external_id && (
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              width: columnWidths.external_id,
                              minWidth: columnWidths.external_id,
                              maxWidth: columnWidths.external_id,
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                            }}
                          >
                            <EllipsisCell text={row.external_id ?? '-'} />
                          </TableCell>
                        )}
                        {columnVisibility.id && (
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              width: columnWidths.id,
                              minWidth: columnWidths.id,
                              maxWidth: columnWidths.id,
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                            }}
                          >
                            <EllipsisCell text={String(row.id)} />
                          </TableCell>
                        )}
                        {columnVisibility.email && (
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              width: columnWidths.email,
                              minWidth: columnWidths.email,
                              maxWidth: columnWidths.email,
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                            }}
                          >
                            <EllipsisCell text={row.email ?? '-'} />
                          </TableCell>
                        )}
                        {columnVisibility.client_tags && (
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              width: columnWidths.client_tags,
                              minWidth: columnWidths.client_tags,
                              maxWidth: columnWidths.client_tags,
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                            }}
                          >
                            <EllipsisCell text={displayClientTags(row)} />
                          </TableCell>
                        )}
                        {columnVisibility.client_divisions && (
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              width: columnWidths.client_divisions,
                              minWidth: columnWidths.client_divisions,
                              maxWidth: columnWidths.client_divisions,
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                            }}
                          >
                            <EllipsisCell text={displayClientDivisions(row)} />
                          </TableCell>
                        )}
                        {columnVisibility.status && (
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              width: columnWidths.status,
                              minWidth: columnWidths.status,
                              maxWidth: columnWidths.status,
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                            }}
                          >
                            <Tooltip
                              title={statusText}
                              placement="top-start"
                              enterDelay={400}
                              disableHoverListener={!statusText || statusText === '-'}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  maxWidth: '100%',
                                  px: 1.25,
                                  py: 0.5,
                                  borderRadius: '999px',
                                  fontSize: 12,
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  ...getStatusStyle(statusText),
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                  }}
                                >
                                  {statusText}
                                </Box>
                              </Box>
                            </Tooltip>
                          </TableCell>
                        )}
                        {columnVisibility.type && (
                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              width: columnWidths.type,
                              minWidth: columnWidths.type,
                              maxWidth: columnWidths.type,
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                            }}
                          >
                            <EllipsisCell text={displayType(row)} />
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      sx={{ ...bodyCellSx, textAlign: 'center', color: '#6B7280', py: 3 }}
                    >
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalRecords}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            className={styles.tablePagination}
            onPageChange={(_, newPage) => {
              const nextPage = newPage + 1;
              setPage(nextPage);
              updateListState({ page: nextPage });
            }}
            onRowsPerPageChange={(e) => {
              const nextRowsPerPage = +e.target.value;
              dispatch(
                setPersistedRowsPerPage({
                  tableKey: TablePaginationKey.CollectorUsers,
                  rowsPerPage: nextRowsPerPage,
                }),
              );
              setPage(1);
              updateListState({ rowsPerPage: nextRowsPerPage, page: 1 });
            }}
          />
        </Paper>
      </Box>

      {dataLoading && users.length > 0 && <Loading dotted />}
    </div>
  );
};

export default Users;
